import api from './client';

export interface ChatConversation {
  id: string;
  title: string;
  /** 会话模式：普通问答（chat）/ 独立智能体（agent），由创建时 body 的 mode 决定 */
  mode: string;
  created_at: string | null;
  updated_at: string | null;
}

export interface ChatConversationListItem extends ChatConversation {
  message_count: number;
}

export interface ChatConversationList {
  total: number;
  conversations: ChatConversationListItem[];
  /** 本页之后是否还有会话（键集翻页，游标=末条 updated_at+id） */
  has_more: boolean;
}

export interface ChatMessage {
  id: string;
  conversation_id: string;
  role: 'user' | 'assistant';
  content: string;
  refs: string | null; // 引用列表 JSON（与 /llm/chat 的 sources 事件同构）
  model: string | null;
  created_at: string | null;
}

export interface ChatConversationDetail extends ChatConversation {
  messages: ChatMessage[];
  /** True=还有更早消息，用最早一条的 id 作 before_id 向前翻 */
  has_more: boolean;
}

// 助手消息里的引用条目（检索来源）
export interface ChatSource {
  id: string;
  title: string;
  preview?: string;
  source_type?: string;
}

export function parseMessageRefs(refs: string | null): ChatSource[] {
  if (!refs) return [];
  try {
    const parsed = JSON.parse(refs);
    // 批⑤ 取证回执（agent_evidence 键）不是引用源：深度研究 refs 末位标记条目
    // 在此过滤，防历史页渲染幽灵引用行；agent 会话的 dict 命名空间形态由
    // Array.isArray 天然挡掉
    return Array.isArray(parsed)
      ? parsed.filter((r) => r && typeof r === 'object' && !('agent_evidence' in r))
      : [];
  } catch {
    return [];
  }
}

export const chatApi = {
  // mode 过滤（如 mode=agent 只列智能体会话）；不传保持旧行为。
  // 分页：默认后端回最近 50 条；cursor 传上一页末条的 updated_at+id 键集翻页
  listConversations: (
    mode?: string,
    page?: { limit?: number; beforeUpdatedAt?: string; beforeId?: string },
  ) =>
    api.get<ChatConversationList>('/api/v1/chat/conversations', {
      params: {
        ...(mode ? { mode } : {}),
        ...(page?.limit ? { limit: page.limit } : {}),
        ...(page?.beforeUpdatedAt ? { before_updated_at: page.beforeUpdatedAt } : {}),
        ...(page?.beforeId ? { before_id: page.beforeId } : {}),
      },
    }),
  // mode 缺省不带，保持旧调用方行为不变
  createConversation: (title?: string, mode?: string) =>
    api.post<ChatConversation>('/api/v1/chat/conversations', {
      ...(title ? { title } : {}),
      ...(mode ? { mode } : {}),
    }),
  // 默认只回最近 100 条消息；beforeId 传当前最早一条的 id 向前翻更早
  getConversation: (id: string, page?: { limit?: number; beforeId?: string }) =>
    api.get<ChatConversationDetail>(`/api/v1/chat/conversations/${id}`, {
      params: page
        ? {
            ...(page.limit ? { limit: page.limit } : {}),
            ...(page.beforeId ? { before_id: page.beforeId } : {}),
          }
        : undefined,
    }),
  renameConversation: (id: string, title: string) =>
    api.patch<ChatConversation>(`/api/v1/chat/conversations/${id}`, { title }),
  deleteConversation: (id: string) =>
    api.delete(`/api/v1/chat/conversations/${id}`),
};
